<template>
    <v-dialog v-model="dialogSwitch" persistent scrollable width="500">
        <v-form v-model="formData.valid" class="mb-3" validate-on="input" @submit.prevent="submitForm">
            <v-card>
                <v-card-title id="heading" class="d-flex justify-space-between">
                    <h4 v-if="otaFile">Update "{{ otaFile.name }}" OTA File</h4>
                    <h4 v-else>Add New OTA File</h4>
                    <v-btn density="compact" icon="mdi-close" variant="flat" @click="$emit('update:modelValue',false)"/>
                </v-card-title>

                <v-container>
                    <v-alert v-if="errors.length" class="mb-3 bg-error" closable close-label="Close Alert"
                             variant="tonal">
                        <p v-for="error in errors">{{ error }}</p>
                    </v-alert>
                    <v-row>
                        <v-col class="pb-0" cols="12">
                            <v-select v-model="formData['product_sku']" :items="productSkus" :rules="[vRules.required]"
                                      item-title="sku" item-value="id" label="Product Sku"
                                      @update:modelValue="()=>{ formData['ota_component'] = null; fetchComponentsList()}">
                            </v-select>
                        </v-col>
                        <v-col class="pb-0" cols="12">
                            <v-select v-model="formData['ota_component']" :items="components" :rules="[vRules.required]"
                                      item-title="name" item-value="id" label="OTA Component"/>
                        </v-col>

                        <v-col class="pb-0" cols="12">
                            <v-text-field v-model="formData['name']" autofocus :rules="[vRules.required]" label="Name"/>
                        </v-col>
                        <v-col class="pb-0" cols="12">
                            <v-text-field v-model="formData['version']" :rules="[vRules.required]" label="Version"/>
                        </v-col>
                        <v-col class="pb-0" cols="12">
                            <v-checkbox v-model="formData['priority']" label="Priority"/>
                        </v-col>
                        <v-col class="pb-0" cols="12">
                            <v-textarea v-model="formData['notes']" label="Notes" rows="2" auto-grow/>
                        </v-col>

                        <v-col class="pb-0" cols="12">
                            <small v-if="otaFile" class="font-weight-bold">{{ otaFile.file_name }} ({{
                                    otaFile.file_size
                                }} bytes)</small>
                            <div class="border" style="height:200px">
                                <label v-if="isDragAndDropCapable"
                                       class="d-block w-100 h-100 cursor-pointer d-flex flex-column align-center justify-center bg-grey-lighten-5"
                                       for="assetsFieldHandle"
                                       @dragleave="dragleave"
                                       @dragover="dragover" @drop="drop">
                                    <v-file-input id="assetsFieldHandle"
                                                  v-model="formData['file']"
                                                  class="opacity-0 overflow-hidden position-absolute"
                                                  @update:model-value="fileChange"/>
                                    <p>Drop your file here, or <span class="text-primary">Browse</span></p>

                                    <v-chip v-if="formData['file']" size="x-small">
                                        <span class="me-1">{{ formData['file'].name }}</span>
                                        <v-icon icon="mdi-close-circle" @click="formData['file'] = null"/>
                                    </v-chip>
                                </label>
                                <div v-else>
                                    <v-file-input v-model="formData['file']" label="Binary File"/>
                                </div>
                            </div>
                        </v-col>
                    </v-row>
                </v-container>

                <v-card-actions class="px-4 d-flex justify-end">
                    <v-btn size="small" type="button"
                           @click="$emit('update:modelValue',false)">
                        Close
                    </v-btn>
                    <v-btn :disabled="!formData.valid" color="primary" size="small" type="submit">Save</v-btn>
                </v-card-actions>
            </v-card>
        </v-form>
    </v-dialog>
</template>

<script setup>
import {ref, reactive, onMounted} from "vue";
import {send} from "@/composables/apiClient";
import {useSnackbarStore} from "@/stores/snackBarStore";

const snackbar = useSnackbarStore();
const props = defineProps({
    modelValue: Boolean,
    otaFile: Object,
});

const formData = reactive({
    valid: false,
    file: null,
    product_sku: null,
    ota_component: null,
    name: props.otaFile?.name ?? null,
    version: props.otaFile?.version ?? null,
    priority: !!parseInt(props.otaFile?.priority ?? 0),
    notes: props.otaFile?.notes ?? null,
});

const emits = defineEmits(['update:modelValue', 'addFile', 'updateFile']);
const dialogSwitch = ref(props.modelValue);
const productSkus = ref([]);
const components = ref([]);
const errors = ref([]);

// Drag & Drop Snippet
const isDragAndDropCapable = () => {
    const div = document.createElement("div");
    return (("draggable" in div || "ondragstart" in div && "ondrop" in div)
        && "FormData" in window && "FileReader" in window);

};
const dragover = (event) => {
    event.preventDefault();
    if (!event.currentTarget.classList.contains('bg-grey-lighten-4')) {
        event.currentTarget.classList.remove('bg-grey-lighten-5');
        event.currentTarget.classList.add('bg-grey-lighten-4');
    }
}
const dragleave = (event) => {
    event.preventDefault();
    event.currentTarget.classList.add('bg-grey-lighten-5');
    event.currentTarget.classList.remove('bg-grey-lighten-4');
}
const drop = (event) => {
    event.preventDefault();
    formData['file'] = event.dataTransfer.files[0];
    dragleave(event);
    fileChange();
}
const fileChange = () => {
    if (formData['file'] && formData['file']['name'] && !formData['name']) {
        formData['name'] = formData['file']['name'];
    }
}
onMounted(async () => {
    await fetchProductSkusList().then(() => {
        if (props.otaFile) {
            formData['product_sku'] = parseInt(props.otaFile?.product_sku_id);
            fetchComponentsList().then(() => {
                formData['ota_component'] = parseInt(props.otaFile?.ota_component_id);
            });
        }
    });
})
const fetchProductSkusList = () => {
    return send('get', `product-skus`, {list: true})
        .then((response) => {
            productSkus.value = response.data.data.product_skus;
        }).catch((error) => {
            snackbar.showSnackbar('Product skus list could not be loaded', 'error')
        })
}
const fetchComponentsList = () => {
    return send('get', `admin/ota/components`, {list: true, product_sku: formData['product_sku']})
        .then((response) => {
            components.value = response.data.data;
        }).catch((error) => {
            snackbar.showSnackbar('OTA Components list could not be loaded', 'error')
        })
}
const submitForm = () => {
    errors.value = [];
    if (!props.otaFile && !formData['file']) {
        snackbar.showSnackbar("Binary file is required", 'error');
        return false;
    }

    formData['priority'] = ~~formData['priority'];
    if (props.otaFile) {
        send('post', `admin/ota/files/${props.otaFile.id}`, {_method: 'put', ...formData}, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        }).then((response) => {
            snackbar.showSnackbar(response.data.message);
            if (response.data.success) {
                emits('updateFile', response.data.data);
                emits('update:modelValue', false);
            }
        }).catch(function (errorResponse) {
            if (errorResponse.response?.data?.errors) {
                let validationErrors = errorResponse.response.data.errors;
                Object.keys(validationErrors).forEach((key) => {
                    errors.value.push(validationErrors[key][0]);
                });
            } else if (errorResponse.response?.data.message) {
                errors.value.push(errorResponse.response.data.message);
            }
            snackbar.showSnackbar('Something went wrong', 'error');
            document.getElementById('heading').scrollIntoView({behavior: 'smooth'});
        })
    } else {
        send('post', `admin/ota/files`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        }).then((response) => {
            snackbar.showSnackbar(response.data.message);
            if (response.data.success) {
                emits('addFile', response.data.data);
                emits('update:modelValue', false);
            }
        }).catch(function (errorResponse) {
            console.log(errorResponse);
            if (errorResponse.response?.data.errors) {
                let validationErrors = errorResponse.response.data.errors;
                Object.keys(validationErrors).forEach((key) => {
                    errors.value.push(validationErrors[key][0]);
                });
            } else if (errorResponse.response?.data.message) {
                errors.value.push(errorResponse.response.data.message);
            }
            snackbar.showSnackbar('Something went wrong', 'error');
            document.getElementById('heading').scrollIntoView({behavior: 'smooth'});
        })
    }
}

</script>
